import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Link } from 'react-router-dom';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { surgeryService } from '../../../services/surgeryService';
import TheatreDayView from './TheatreDayView';

const useStyles = makeStyles(() => ({
  root: {
    margin: 22,
    padding: 20,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  navGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  dateLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    minWidth: 180,
    textAlign: 'center',
  },
  controlsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  viewToggle: {
    '& .MuiButton-root': {
      textTransform: 'none',
      fontSize: '0.8rem',
      padding: '4px 14px',
    },
  },
  activeViewBtn: {
    backgroundColor: '#3f51b5',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#303f9f',
    },
  },
  filterSelect: {
    fontSize: '0.85rem',
    minWidth: 140,
    '& .MuiSelect-select': {
      padding: '6px 12px',
    },
  },
  scheduleBtn: {
    textTransform: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    backgroundColor: '#3f51b5',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#303f9f',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  thCorner: {
    width: 120,
    padding: '10px 8px',
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textAlign: 'left',
  },
  thDay: {
    padding: '8px 6px',
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #e0e0e0',
    borderRight: '1px solid #f0f0f0',
    textAlign: 'center',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  thDayName: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#333',
    textTransform: 'uppercase',
  },
  thDayDate: {
    fontSize: '0.7rem',
    color: '#777',
    marginTop: 2,
  },
  thDayToday: {
    backgroundColor: '#e8eaf6',
  },
  theatreRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  theatreNameCell: {
    padding: '10px 8px',
    borderBottom: '1px solid #f0f0f0',
    borderRight: '1px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#333',
    verticalAlign: 'top',
  },
  theatreType: {
    fontSize: '0.65rem',
    color: '#999',
    fontWeight: 400,
    marginTop: 2,
  },
  dayCell: {
    padding: 6,
    borderBottom: '1px solid #f0f0f0',
    borderRight: '1px solid #f0f0f0',
    verticalAlign: 'top',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dayCellToday: {
    backgroundColor: '#f3f4fb',
    '&:hover': {
      backgroundColor: '#e8eaf6',
    },
  },
  cellCard: {
    padding: 6,
    borderRadius: 4,
    backgroundColor: '#fff',
    border: '1px solid #eee',
  },
  surgeryCount: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 4,
  },
  utilBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
    overflow: 'hidden',
    marginBottom: 3,
  },
  utilFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  utilText: {
    fontSize: '0.65rem',
    fontWeight: 500,
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyCell: {
    fontSize: '0.7rem',
    color: '#ccc',
    textAlign: 'center',
    padding: 8,
  },
}));

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_MAP = { 0: 'booked', 1: 'in_progress', 2: 'completed', 9: 'cancelled' };

function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function toDateStr(d) {
  return d.toISOString().split('T')[0];
}

function formatDate(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

function formatWeekLabel(monday) {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const mStr = monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const sStr = sunday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${mStr} - ${sStr}`;
}

function formatDayLabel(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function getUtilColor(pct) {
  if (pct > 85) return '#F44336';
  if (pct >= 60) return '#FF9800';
  return '#4CAF50';
}

function dedupeBookings(bookings) {
  const seen = new Set();
  return (bookings || []).filter((booking) => {
    const key = booking.booking_id
      ?? `${booking.theatre_name}-${booking.booking_date}-${booking.start_time}-${booking.surgery_id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Build week-view summary (surgery count + utilization%) per theatre per day from booking list
function buildWeekSummary(bookings, theatres, weekDates) {
  bookings = dedupeBookings(bookings);
  // key: theatreId -> dayIdx -> { surgeries, totalMins }
  const summary = {};
  theatres.forEach((t) => {
    summary[t.theatre_id] = {};
    weekDates.forEach((_, i) => {
      summary[t.theatre_id][i] = { surgeries: 0, totalMins: 0 };
    });
  });

  const theatreByName = {};
  theatres.forEach((t) => { theatreByName[t.name] = t.theatre_id; });

  bookings.forEach((b) => {
    const theatreId = theatreByName[b.theatre_name];
    if (theatreId == null) return;
    const bdStr = String(b.booking_date).slice(0, 10);
    const dayIdx = weekDates.findIndex((d) => toDateStr(d) === bdStr);
    if (dayIdx < 0) return;

    summary[theatreId][dayIdx].surgeries += 1;

    // calc duration in mins
    if (b.start_time && b.end_time) {
      const [sh, sm] = b.start_time.split(':').map(Number);
      const [eh, em] = b.end_time.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins > 0) summary[theatreId][dayIdx].totalMins += mins;
    }
  });

  // Convert totalMins to utilization % (theatre runs 6:00–20:00 = 840 mins)
  const THEATRE_MINS = 840;
  const result = {};
  theatres.forEach((t) => {
    result[t.theatre_id] = {};
    weekDates.forEach((_, i) => {
      const cell = summary[t.theatre_id][i];
      result[t.theatre_id][i] = {
        surgeries: cell.surgeries,
        utilization: cell.totalMins > 0 ? Math.min(Math.round((cell.totalMins / THEATRE_MINS) * 100), 100) : 0,
      };
    });
  });
  return result;
}

// Transform flat booking list into TheatreDayView slot format for a given date
function buildDaySlots(bookings, dateObj) {
  const dateStr = toDateStr(dateObj);
  const slots = dedupeBookings(bookings)
    .filter((b) => {
      const bd = String(b.booking_date).slice(0, 10);
      return bd === dateStr && b.status !== 9;
    })
    .map((b) => ({
      theatre: b.theatre_name,
      time: `${b.start_time}-${b.end_time}`,
      surgery_id: b.surgery_id,
      procedure: b.service_name || '',
      patient: `${b.patient_first_name || ''} ${b.patient_last_name || ''}`.trim() || '',
      surgeon: b.surgeon_name || '',
      status: STATUS_MAP[b.status] || 'booked',
    }));
  return { date: dateStr, slots };
}

const TheatreCalendar = () => {
  const classes = useStyles();
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [theatres, setTheatres] = useState([]);
  const [weekBookings, setWeekBookings] = useState([]);
  const [loadingTheatres, setLoadingTheatres] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [theatreFilter, setTheatreFilter] = useState('all');

  const monday = useMemo(() => getMonday(currentDate), [currentDate]);

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [monday]);

  // Load theatres once
  useEffect(() => {
    setLoadingTheatres(true);
    surgeryService.listTheatres()
      .then((res) => {
        const data = res.data;
        setTheatres(Array.isArray(data) ? data : (data?.theatres || []));
      })
      .catch(() => setTheatres([]))
      .finally(() => setLoadingTheatres(false));
  }, []);

  // Load calendar for the current week whenever week changes, and auto-refresh every 60s
  useEffect(() => {
    const dateFrom = toDateStr(weekDates[0]);
    const dateTo = toDateStr(weekDates[6]);

    const load = (silent = false) => {
      if (!silent) setLoadingCalendar(true);
      surgeryService.theatreCalendar({ date_from: dateFrom, date_to: dateTo })
        .then((res) => {
          const data = res.data;
          setWeekBookings(Array.isArray(data) ? data : []);
        })
        .catch(() => {})
        .finally(() => { if (!silent) setLoadingCalendar(false); });
    };

    load(false);
    const timer = setInterval(() => load(true), 60000);
    return () => clearInterval(timer);
  }, [monday]);

  const filteredTheatres = useMemo(() => {
    if (theatreFilter === 'all') return theatres;
    return theatres.filter((t) => t.theatre_id === Number(theatreFilter));
  }, [theatres, theatreFilter]);

  const weekData = useMemo(() => {
    return buildWeekSummary(weekBookings, filteredTheatres, weekDates);
  }, [weekBookings, filteredTheatres, weekDates]);

  const daySlotData = useMemo(() => {
    return buildDaySlots(weekBookings, currentDate);
  }, [weekBookings, currentDate]);

  const navigateDate = useCallback((direction) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      if (viewMode === 'week') {
        next.setDate(next.getDate() + direction * 7);
      } else {
        next.setDate(next.getDate() + direction);
      }
      return next;
    });
  }, [viewMode]);

  const goToToday = useCallback(() => setCurrentDate(new Date()), []);

  const handleCellClick = useCallback((date) => {
    setCurrentDate(date);
    setViewMode('day');
  }, []);

  const today = new Date();

  if (loadingTheatres) {
    return (
      <div>
        <PageTitle title="Theatre Calendar" />
        <SubHeader title="Surgery" subTitle="Theatre Calendar" />
        <Paper className={classes.root} elevation={1}>
          <div className={classes.loadingBox}><CircularProgress /></div>
        </Paper>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Theatre Calendar" />
      <SubHeader title="Surgery" subTitle="Theatre Calendar" />
      <Paper className={classes.root} elevation={1}>
        {/* Header Controls */}
        <div className={classes.headerRow}>
          <div className={classes.navGroup}>
            <IconButton size="small" onClick={() => navigateDate(-1)}>
              <ChevronLeft />
            </IconButton>
            <Typography className={classes.dateLabel}>
              {viewMode === 'week' ? formatWeekLabel(monday) : formatDayLabel(currentDate)}
            </Typography>
            <IconButton size="small" onClick={() => navigateDate(1)}>
              <ChevronRight />
            </IconButton>
            <Button size="small" onClick={goToToday} style={{ marginLeft: 8, textTransform: 'none', fontSize: '0.8rem' }}>
              Today
            </Button>
          </div>
          <div className={classes.controlsGroup}>
            <ButtonGroup size="small" className={classes.viewToggle}>
              <Button
                className={viewMode === 'week' ? classes.activeViewBtn : ''}
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
              <Button
                className={viewMode === 'day' ? classes.activeViewBtn : ''}
                onClick={() => setViewMode('day')}
              >
                Day
              </Button>
            </ButtonGroup>
            <Select
              value={theatreFilter}
              onChange={(e) => setTheatreFilter(e.target.value)}
              variant="outlined"
              className={classes.filterSelect}
            >
              <MenuItem value="all">All Theatres</MenuItem>
              {theatres.map((t) => (
                <MenuItem key={t.theatre_id} value={t.theatre_id}>{t.name}</MenuItem>
              ))}
            </Select>
            <Button
              component={Link}
              to="/surgery/schedule"
              variant="contained"
              size="small"
              className={classes.scheduleBtn}
            >
              Schedule Surgery
            </Button>
          </div>
        </div>

        {loadingCalendar && (
          <div className={classes.loadingBox} style={{ minHeight: 80 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginLeft: 12, color: '#888' }}>Loading schedule...</Typography>
          </div>
        )}

        {/* Week View */}
        {viewMode === 'week' && !loadingCalendar && (
          <div className={classes.tableWrapper}>
            {filteredTheatres.length === 0 ? (
              <Typography style={{ color: '#999', textAlign: 'center', padding: 40 }}>
                No theatres configured. Add theatres to see the calendar.
              </Typography>
            ) : (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.thCorner}>Theatre</th>
                    {weekDates.map((date, i) => {
                      const isToday = isSameDay(date, today);
                      return (
                        <th key={i} className={`${classes.thDay} ${isToday ? classes.thDayToday : ''}`}>
                          <div className={classes.thDayName}>{DAY_NAMES[i]}</div>
                          <div className={classes.thDayDate}>{formatDate(date)}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredTheatres.map((theatre) => (
                    <tr key={theatre.theatre_id} className={classes.theatreRow}>
                      <td className={classes.theatreNameCell}>
                        {theatre.name}
                        {theatre.location && (
                          <div className={classes.theatreType}>{theatre.location}</div>
                        )}
                      </td>
                      {weekDates.map((date, dayIdx) => {
                        const cell = weekData[theatre.theatre_id]?.[dayIdx] || { utilization: 0, surgeries: 0 };
                        const isToday = isSameDay(date, today);
                        const utilColor = getUtilColor(cell.utilization);
                        return (
                          <td
                            key={dayIdx}
                            className={`${classes.dayCell} ${isToday ? classes.dayCellToday : ''}`}
                            onClick={() => handleCellClick(date)}
                          >
                            {cell.surgeries > 0 ? (
                              <Tooltip title={`${theatre.name} — click for day view`} arrow>
                                <div className={classes.cellCard}>
                                  <div className={classes.surgeryCount}>
                                    {cell.surgeries} {cell.surgeries === 1 ? 'surgery' : 'surgeries'}
                                  </div>
                                  <div className={classes.utilBar}>
                                    <div
                                      className={classes.utilFill}
                                      style={{ width: `${cell.utilization}%`, backgroundColor: utilColor }}
                                    />
                                  </div>
                                  <div className={classes.utilText} style={{ color: utilColor }}>
                                    {cell.utilization}% utilised
                                  </div>
                                </div>
                              </Tooltip>
                            ) : (
                              <div className={classes.emptyCell}>—</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Day View */}
        {viewMode === 'day' && !loadingCalendar && (
          <TheatreDayView
            date={currentDate}
            calendarData={daySlotData}
            theatres={filteredTheatres}
          />
        )}
      </Paper>
    </div>
  );
};

export default TheatreCalendar;
